import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Share2, Send, Wand2, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ContentType, Platform } from '../types';
import { PostPreviewPanel } from '../../common/PostPreviewPanel';
import { PlatformMediaGrid, type PreviewMediaItem } from '../PlatformMediaGrid';
import { NotionPropertyRow } from './NotionPropertyRow';
import { NotionBanner } from './NotionBanner';

export interface NotionComposerShellProps {
  isEditing: boolean;
  contentType: ContentType;
  title: string;
  onTitleChange: (title: string) => void;
  selectedPlatforms: string[];
  platforms: Platform[];
  isValid: boolean;
  isPublishValid: boolean;
  isDirty?: boolean;
  isDraft?: boolean;
  postStatus?: string;
  onSaveDraft: () => void;
  onPublish: () => void;
  onSchedule?: () => void;
  isLoading?: boolean;
  showScheduleButton?: boolean;
  preflightErrors?: Array<{ platform: string; message: string; code?: string; fixable?: boolean; fix_type?: string; media_id?: string }>;
  preflightWarnings?: Array<{ platform: string; message: string; code?: string; fixable?: boolean; fix_type?: string; media_id?: string }>;
  onAutoFixMedia?: (mediaId: string, platform: string, fixType: string) => void;
  onOpenAgentFix?: (mediaId: string, platform: string, fixType: string) => void;
  _accountAvatarUrl?: string;
  accountName?: string;
  previewCaption?: string;
  /** Per-platform caption overrides used by the phone previews. Each
   *  entry replaces `previewCaption` for that specific platform's
   *  mockup. If a platform has no entry, the shared `previewCaption`
   *  is used. */
  previewPlatformCaptions?: Record<string, string>;
  previewImageUrl?: string;
  previewMediaItems?: PreviewMediaItem[];
  mediaCount?: number;
  children: React.ReactNode;
}

const CONTENT_TYPE_EMOJIS: Record<ContentType, string> = {
  post: '📝',
  story: '🎬',
  reel: '🎞️',
  carousel: '🖼️',
  igtv: '📺',
  short: '⚡',
};

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  post: 'Post',
  story: 'Story',
  reel: 'Reel',
  carousel: 'Carousel',
  igtv: 'IGTV',
  short: 'Short',
};

export const NotionComposerShell: React.FC<NotionComposerShellProps> = ({
  isEditing,
  contentType,
  title,
  onTitleChange,
  selectedPlatforms,
  platforms,
  isValid,
  isPublishValid,
  isDirty = true,
  isDraft = false,
  postStatus = 'draft',
  onSaveDraft,
  onPublish,
  onSchedule,
  isLoading = false,
  showScheduleButton = false,
  preflightErrors = [],
  preflightWarnings = [],
  onAutoFixMedia,
  onOpenAgentFix,
  _accountAvatarUrl,
  accountName = 'your_account',
  previewCaption = '',
  previewPlatformCaptions = {},
  previewImageUrl,
  previewMediaItems = [],
  mediaCount = 0,
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const contentTypeLabel = CONTENT_TYPE_LABELS[contentType];

  const statusMeta: { label: string; chip: string; sub: string; crumb: string } = (() => {
    switch (postStatus) {
      case 'published':
        return {
          label: t('comp_notion_status_published', 'Published'),
          chip: 'bg-emerald-100 text-emerald-800',
          sub: t('comp_notion_status_sub_published', 'published'),
          crumb: t('comp_notion_crumb_published', 'Published'),
        };
      case 'scheduled':
        return {
          label: t('comp_notion_status_scheduled', 'Scheduled'),
          chip: 'bg-blue-100 text-blue-800',
          sub: t('comp_notion_status_sub_scheduled', 'scheduled, not yet published'),
          crumb: t('comp_notion_crumb_scheduled', 'Scheduled'),
        };
      case 'posting':
        return {
          label: t('comp_notion_status_posting', 'Posting'),
          chip: 'bg-amber-100 text-amber-800',
          sub: t('comp_notion_status_sub_posting', 'publishing now'),
          crumb: t('comp_notion_crumb_posting', 'Posting'),
        };
      case 'failed':
        return {
          label: t('comp_notion_status_failed', 'Failed'),
          chip: 'bg-red-100 text-red-800',
          sub: t('comp_notion_status_sub_failed', 'publish failed'),
          crumb: t('comp_notion_crumb_failed', 'Failed'),
        };
      case 'draft':
      default:
        return {
          label: t('comp_notion_status_draft', 'Draft'),
          chip: 'bg-yellow-100 text-yellow-800',
          sub: t('comp_notion_status_sub_draft', 'not scheduled, not published'),
          crumb: t('comp_notion_crumb_drafts', 'Drafts'),
        };
    }
  })();
  // isDraft is retained for legacy gating of save-draft actions by callers
  void isDraft;

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* Top breadcrumb bar */}
      <div className="flex-shrink-0 z-20 bg-white flex items-center justify-between gap-4 h-11 px-6 border-b border-slate-200">
        <div className="flex items-center gap-2 text-sm text-slate-600 min-w-0 flex-1">
          <button
            onClick={() => navigate('/posts')}
            className="hover:text-slate-900 transition-colors flex items-center gap-1 flex-shrink-0"
            type="button"
            aria-label="Back to posts"
          >
            <ChevronLeft className="h-4 w-4" />
            Posts
          </button>
          <span className="text-slate-400 flex-shrink-0">/</span>
          <span className="flex-shrink-0">{statusMeta.crumb}</span>
          <span className="text-slate-400 flex-shrink-0">/</span>
          <span className="text-slate-900 font-medium truncate min-w-0">{title || '(untitled)'}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {isEditing ? t('comp_notion_edited_recently', 'Edited recently') : ''}
          </span>
          <button
            type="button"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
            title="Share"
          >
            <Share2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onSaveDraft}
            disabled={!isValid || isLoading || !isDirty}
            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            {isDirty ? t('comp_save_draft', 'Save as draft') : t('comp_saved', 'Saved')}
          </button>
          {showScheduleButton ? (
            <button
              type="button"
              onClick={onSchedule}
              disabled={!isPublishValid || isLoading}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {t('comp_schedule_post', 'Schedule')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onPublish}
              disabled={!isPublishValid || isLoading}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              <Send className="h-3.5 w-3.5" />
              {t('comp_publish_now', 'Publish')}
            </button>
          )}
        </div>
      </div>

      {/* Main content grid */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-0 max-w-7xl mx-auto w-full">
        {/* Left: Main content */}
        <div className="border-r border-slate-200 lg:border-r-1 px-12 py-8 overflow-y-auto min-h-0">
          {/* Title section with emoji */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-4xl leading-none opacity-90 shrink-0" aria-hidden="true">
                {CONTENT_TYPE_EMOJIS[contentType]}
              </span>
              <input
                type="text"
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder={t('comp_notion_title_placeholder', 'Enter post title...')}
                className="flex-1 min-w-0 text-4xl font-bold text-slate-900 placeholder-slate-300 bg-transparent border-0 outline-none focus:ring-0"
                spellCheck="false"
              />
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span className={`inline-block px-2 py-1 ${statusMeta.chip} rounded text-xs font-semibold`}>
                ● {statusMeta.label}
              </span>
              <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
                {contentTypeLabel}
              </span>
              {contentType === 'story' && (
                <span className="inline-block px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-semibold">
                  24h ephemeral
                </span>
              )}
              <span className="text-slate-400">
                · {0} / {contentType === 'story' ? '2,200' : '5,000'} characters
              </span>
            </div>
          </div>

          {/* Properties section */}
          <div className="mb-8 border-t border-slate-200">
            <NotionPropertyRow
              label={t('comp_notion_status', 'Status')}
              icon="◎"
            >
              <span className={`px-2 py-1 ${statusMeta.chip} rounded text-xs font-semibold`}>
                ● {statusMeta.label}
              </span>
              <span className="text-slate-500 text-xs">
                {statusMeta.sub}
              </span>
            </NotionPropertyRow>

            <NotionPropertyRow
              label={t('comp_notion_format', 'Format')}
              icon="⬚"
            >
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
                {contentTypeLabel}
              </span>
              <span className="text-slate-500 text-xs">
                {isEditing
                  ? 'Locked — changing format is a new post'
                  : 'Click to change'}
              </span>
            </NotionPropertyRow>

            <NotionPropertyRow
              label={t('comp_notion_platforms', 'Platforms')}
              icon="◈"
            >
              <div className="flex flex-wrap gap-2">
                {selectedPlatforms.length === 0 ? (
                  <span className="text-slate-500 text-xs">Not selected</span>
                ) : (
                  selectedPlatforms.map((pId) => {
                    const p = platforms.find((x) => x.id === pId);
                    return (
                      <span
                        key={pId}
                        className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs font-semibold"
                      >
                        {p?.icon} {p?.name}
                      </span>
                    );
                  })
                )}
              </div>
            </NotionPropertyRow>

            <NotionPropertyRow
              label={t('comp_notion_media', 'Media')}
              icon="◱"
            >
              <span className="text-slate-500 text-xs">
                {mediaCount > 0
                  ? t('compose_media_count', '{{count}} attached', { count: mediaCount })
                  : t('compose_no_media', 'No media')}
              </span>
            </NotionPropertyRow>
          </div>

          {/* Validation banners */}
          {preflightErrors.length > 0 && (
            <div className="mb-6 space-y-2">
              {preflightErrors.map((err, i) => (
                <NotionBanner key={i} type="error">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {err.platform ? (
                        <>
                          <strong>{err.platform}:</strong> {err.message}
                        </>
                      ) : (
                        err.message
                      )}
                    </div>
                    {err.fixable && err.media_id && (
                      <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                        {onAutoFixMedia && (
                          <button
                            type="button"
                            onClick={() => onAutoFixMedia(err.media_id!, err.platform, err.fix_type || 'aspect_ratio')}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
                            title={t('preflight_auto_fix', 'Automatically fix this issue')}
                          >
                            <Wrench className="w-3 h-3" />
                            {t('preflight_fix_now', 'Fix Now')}
                          </button>
                        )}
                        {onOpenAgentFix && (
                          <button
                            type="button"
                            onClick={() => onOpenAgentFix(err.media_id!, err.platform, err.fix_type || 'aspect_ratio')}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                            title={t('preflight_ai_fix', 'Let AI agent fix this')}
                          >
                            <Wand2 className="w-3 h-3" />
                            {t('preflight_ai_fix_label', 'AI Fix')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </NotionBanner>
              ))}
            </div>
          )}
          {preflightWarnings.length > 0 && (
            <div className="mb-6 space-y-2">
              {preflightWarnings.map((warn, i) => (
                <NotionBanner key={i} type="warning">
                  {warn.platform ? (
                    <>
                      <strong>{warn.platform}:</strong> {warn.message}
                    </>
                  ) : (
                    warn.message
                  )}
                </NotionBanner>
              ))}
            </div>
          )}

          {/* Collapsible content blocks */}
          <div className="border-b border-slate-200">
            {children}
          </div>

          <div style={{ height: '60px' }} />
        </div>

        {/* Right: Sidebar (independent scroll) */}
        <div className="overflow-y-auto min-h-0 border-t lg:border-t-0 lg:border-l border-slate-200 px-6 py-8">
          <div className="space-y-6">
            {/* Phone preview — platform-specific grid for multi-image, classic phone for single */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                {t('comp_notion_preview', 'Preview')}
              </h3>
              {previewMediaItems.length >= 1 && selectedPlatforms.length > 0 ? (
                <div className="space-y-5">
                  {selectedPlatforms.map((platformId) => {
                    const p = platforms.find((x) => x.id === platformId);
                    // Each phone mockup renders its own per-platform
                    // override text — falls back to the shared caption
                    // when the user hasn't tailored that platform yet.
                    const platformCaption =
                      previewPlatformCaptions[platformId] || previewCaption;
                    return (
                      <div key={platformId} className="mx-auto w-full max-w-[360px]">
                        {/* Platform label chip */}
                        <div className="mb-2 flex justify-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">
                            {p?.icon && <span aria-hidden="true">{p.icon}</span>}
                            {p?.name || platformId}
                          </span>
                        </div>
                        {/* Phone frame — content flows naturally, sidebar scrolls */}
                        <div className="border-[8px] border-black rounded-[36px] overflow-hidden bg-white shadow-xl relative">
                          {/* Dynamic Island */}
                          <div className="bg-white flex items-center justify-center pt-1.5 pb-0.5">
                            <div className="w-24 h-[22px] bg-black rounded-full" />
                          </div>
                          {/* Status bar */}
                          <div className="bg-white flex items-center justify-between px-6 pb-1.5">
                            <span className="text-[11px] font-semibold text-slate-900">9:41</span>
                            <div className="flex items-center gap-1.5">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-slate-900" aria-hidden="true"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z" /></svg>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-slate-900" aria-hidden="true"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z" /></svg>
                            </div>
                          </div>
                          {/* App header */}
                          <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-slate-200 bg-white">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-[12px] font-semibold">
                                {(accountName || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-semibold text-slate-900 truncate leading-tight">{accountName}</div>
                                <div className="text-[11px] text-slate-400 leading-tight">{p?.name || platformId}</div>
                              </div>
                              <span className="text-slate-400 text-lg leading-none">&hellip;</span>
                            </div>
                            {/* Platform-specific media */}
                            <PlatformMediaGrid platform={platformId} items={previewMediaItems} />
                            {/* Engagement row */}
                            <div className="px-3.5 py-2.5 flex items-center gap-4 text-slate-800">
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                              <svg className="ml-auto" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                            </div>
                            {/* Like count */}
                            <div className="px-3.5 pb-1.5">
                              <span className="text-[13px] font-semibold text-slate-900">1,234 likes</span>
                            </div>
                            {/* Caption */}
                            {platformCaption && (
                              <div className="px-3.5 pb-3 text-[13px] leading-snug text-slate-900">
                                <div className="whitespace-pre-wrap break-words">
                                  <span className="font-semibold">{accountName}</span>{' '}
                                  {platformCaption}
                                </div>
                              </div>
                            )}
                          {/* Timestamp */}
                          <div className="px-3.5 pb-3">
                            <span className="text-[11px] text-slate-400 uppercase tracking-wide">2 hours ago</span>
                          </div>
                          {/* Home indicator */}
                          <div className="flex justify-center pb-2.5 pt-1.5 bg-white">
                            <div className="w-32 h-1 bg-slate-900 rounded-full opacity-25" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <PostPreviewPanel
                  accountName={accountName}
                  caption={previewCaption}
                  imageUrl={previewImageUrl}
                />
              )}
            </div>

            {/* Platforms picker lives inline in the caption editor
                (ComposerCaptionTabs) — the right rail used to duplicate
                it but that placement was too easy to miss. Keeping a
                single source of truth avoids drift and shortens the
                right rail. */}
          </div>
        </div>
      </div>
    </div>
  );
};
